import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useAuthStore } from '../../store/useAuthStore';
import { useEmployeeStore } from '../../store/useEmployeeStore';
import { useToastStore } from '../../store/useToastStore';
import { showApiError } from '../../errors/showApiError';
import { Button } from '../../components/Shared/Button';
import { FormField } from '../../components/Shared/FormField';

/**
 * Blocks an account whose password it did not choose — an Excel-imported one, or after an
 * admin reset — until the user picks their own.
 *
 * Rendered outside MainLayout on purpose: there is no navigation to escape to, which is
 * the point. `ProtectedRoute` sends users here and keeps sending them until the backend
 * clears `mustChangePassword`.
 */
export function ForcePasswordChange() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const { addToast } = useToastStore();
  const { employee, checkAuth } = useAuthStore();
  const changeOwnPassword = useEmployeeStore((s) => s.changeOwnPassword);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      setMismatch(true);
      return;
    }

    setMismatch(false);
    setSaving(true);

    try {
      await changeOwnPassword({ oldPassword, newPassword });
      // Re-reads /auth/me so the cleared flag reaches the store and the guard lets go.
      await checkAuth();
      addToast(t('auth:forcePasswordChange.success'), 'success');
      navigate('/', { replace: true });
    } catch (error) {
      showApiError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#ece9e3]">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 space-y-5">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-slate-900">
            <KeyRound className="w-5 h-5" strokeWidth={2} />
            <h1 className="text-[24px] font-semibold tracking-tight">
              {t('auth:forcePasswordChange.title')}
            </h1>
          </div>
          <p className="text-[13px] text-slate-600">
            {t('auth:forcePasswordChange.intro', { name: employee?.firstName ?? '' })}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField
            label={t('auth:forcePasswordChange.currentPassword')}
            name="oldPassword"
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            required
          />
          <FormField
            label={t('auth:forcePasswordChange.newPassword')}
            name="newPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <FormField
            label={t('auth:forcePasswordChange.confirmPassword')}
            name="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setMismatch(false);
            }}
            error={mismatch ? t('auth:forcePasswordChange.mismatch') : undefined}
            required
          />

          <Button
            type="submit"
            loading={saving}
            disabled={!oldPassword || !newPassword || !confirmPassword}
            className="w-full"
          >
            {saving
              ? t('auth:forcePasswordChange.saving')
              : t('auth:forcePasswordChange.submit')}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default ForcePasswordChange;
